/**
 * World Cup tab stack layout.
 * Provides shared shell (header + tab bar) with nested route content.
 */

import { useMemo, useState, useEffect, useCallback } from 'react';
import { Platform, View } from 'react-native';
import { Stack, useGlobalSearchParams, useNavigation, usePathname, useRouter } from 'expo-router';
import { StackActions } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { ErrorBoundary } from '../../../components/layout';
import { CompetitionProvider } from '../../../src/competitions/contexts/CompetitionContext';
import { WORLD_CUP_COMPETITION_CONTEXT } from '../../../src/competitions/contexts/competitionContext.constants';
import { WorldCupTabBar } from '../../../components/worldcup/WorldCupTabBar/WorldCupTabBar';
import { WorldCupStandingsModal } from '../../../components/worldcup/WorldCupStandingsModal/WorldCupStandingsModal';
import {
  STANDINGS_VIEWS,
  type StandingsModalView,
} from '../../../components/worldcup/WorldCupStandingsModal/worldCupStandingsModal.constants';
import { WorldCupWelcomeScreen } from '../../../components/worldcup/WorldCupWelcomeScreen/WorldCupWelcomeScreen';
import { WorldCupScreenHeader } from '../../../components/worldcup/WorldCupScreenHeader/WorldCupScreenHeader';
import { PostFirstPickPromptManager } from '../../../components/worldcup/PostFirstPickPromptManager';
import { getWorldcupStyles } from '../../../appStyles/_worldcup.styles';
import { useTheme } from '../../../src/theme/useTheme';
import { getActiveTabFromPath } from '../../../src/worldcup/helpers/worldcupTabRoutes.helper';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useMenuContext } from '../../../src/layout/contexts/MenuContext';
import { useWorldCupLayout } from '../../../src/worldcup/hooks/useWorldCupLayout';
import { useSquadTvModeStore } from '../../../src/worldcup/stores/squadTvModeStore';

const WORLD_CUP_STATS_MODAL_ANIMATION_MS = 240;

export default function WorldCupStackLayout() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const pathname = usePathname();
  const worldCupSearchParams = useGlobalSearchParams();
  const apiClient = useApiClient();
  const { user, isSignedIn, isLoaded } = useSupabaseAuth();
  const activeTab = getActiveTabFromPath(pathname, worldCupSearchParams as Record<string, string | string[] | undefined>);

  const router = useRouter();
  const { handleMenuPress } = useMenuContext();
  const [isStandingsModalVisible, setIsStandingsModalVisible] = useState(false);
  const [standingsInitialView, setStandingsInitialView] = useState<StandingsModalView>(
    STANDINGS_VIEWS.GROUPS,
  );
  const handleStandingsPress = useCallback((view: StandingsModalView) => {
    setStandingsInitialView(view);
    setIsStandingsModalVisible(true);
  }, []);

  // Pressing the World Cup tab while already on it navigates to picks and pops any deep stack.
  useEffect(() => {
    const parent = navigation.getParent();
    if (!parent) return;
    const unsubscribe = parent.addListener(
      'tabPress' as Parameters<typeof parent.addListener>[0],
      (e: unknown) => {
        // Only respond when the worldcup tab button itself was pressed.
        // tabPress fires BEFORE focus changes, so isFocused() is unreliable here —
        // it returns true for WC even when the user pressed Arena.
        // Instead, identify the pressed tab by e.target (the route key).
        const target = (e as { target?: string }).target;
        const parentState = parent.getState();
        const pressedRouteName = parentState?.routes.find((r) => r.key === target)?.name;
        if (pressedRouteName !== 'worldcup') return;
        if (!navigation.isFocused()) return;
        navigation.dispatch(StackActions.popToTop());
        router.replace('/app/worldcup' as never);
      },
    );
    return unsubscribe;
  }, [navigation, router]);

  const userId = user?.id ?? null;

  const { welcomeUi, handleWelcomeFinished, handleTabChange } = useWorldCupLayout({
    isLoaded,
    isSignedIn: isSignedIn ?? null,
    userId,
    activeTab,
    apiClient,
  });

  // On native, match/match-stats use presentation:'modal' (true system overlay) so the
  // WC chrome never needs to be hidden — the modal covers the full screen natively.
  // On web there is no native modal, so we still hide the chrome via isModalChrome.
  const isModalChromePath = Platform.OS === 'web' && (
    pathname.includes('/worldcup/match/') || pathname.includes('/worldcup/match-stats/')
  );
  const isSquadTvModeActive = useSquadTvModeStore((s) => s.isActive);
  const [shouldHideModalChrome, setShouldHideModalChrome] = useState(isModalChromePath);
  const shouldHideShellChrome = shouldHideModalChrome || isSquadTvModeActive;

  useEffect(() => {
    if (isModalChromePath) {
      setShouldHideModalChrome(true);
      return;
    }

    // Fire 50 ms after animation ends so the header/tabbar reappear only after
    // match-stats has fully translated off-screen, not while it is still visible.
    const restoreChromeTimer = setTimeout(() => {
      setShouldHideModalChrome(false);
    }, WORLD_CUP_STATS_MODAL_ANIMATION_MS + 50);

    return () => clearTimeout(restoreChromeTimer);
  }, [isModalChromePath]);

  const styles = useMemo(
    () => getWorldcupStyles(theme, { safeAreaTop: insets.top }),
    [theme, insets.top],
  );

  return (
    <ErrorBoundary>
      {/* Every WC screen below reads its competition from context instead of importing
          the constant. The value is the frozen WC default, so behavior is unchanged. */}
      <CompetitionProvider value={WORLD_CUP_COMPETITION_CONTEXT}>
      <View
        style={shouldHideShellChrome ? styles.containerMatchStatsModal : styles.container}
      >
        <View style={shouldHideShellChrome ? styles.shellHeaderHidden : styles.shellHeader}>
          <WorldCupScreenHeader
            onStandingsPress={handleStandingsPress}
            onMenuPress={handleMenuPress}
          />
        </View>
        <View style={shouldHideShellChrome ? styles.tabBarHidden : undefined}>
          <WorldCupTabBar activeTab={activeTab} onTabChange={handleTabChange} />
        </View>
        <View style={styles.tabContent}>
          <Stack
            screenOptions={({ route }) => ({
              headerShown: false,
              contentStyle: styles.stackContentStyle,
              ...(route.name === 'member-profile'
                ? {
                    animation: Platform.OS === 'web' ? 'fade' : 'slide_from_right',
                    animationDuration: Platform.OS === 'web' ? 150 : 240,
                    gestureEnabled: Platform.OS !== 'web',
                  }
                : route.name === 'match-stats'
                ? {
                    ...(Platform.OS !== 'web' ? { presentation: 'modal' as const } : {}),
                    animation: Platform.OS === 'web' ? 'slide_from_right' : 'slide_from_bottom',
                    animationDuration: WORLD_CUP_STATS_MODAL_ANIMATION_MS,
                    gestureEnabled: true,
                  }
                : route.name === 'match'
                ? {
                    // Native modal covers the full screen (including WC header/tabbar) without
                    // any layout tricks. Web falls back to a regular card push.
                    ...(Platform.OS !== 'web' ? { presentation: 'modal' as const } : {}),
                    animation: Platform.OS === 'web' ? 'fade' : 'slide_from_bottom',
                    gestureEnabled: true,
                  }
                : {
                    animation: 'fade',
                    animationDuration: 150,
                  }),
            })}
          />
        </View>
        <WorldCupStandingsModal
          isVisible={isStandingsModalVisible}
          initialView={standingsInitialView}
          onDismiss={() => setIsStandingsModalVisible(false)}
          testID="worldcup-standings-modal"
        />
        {welcomeUi === 'show' ? (
          <WorldCupWelcomeScreen onEnter={() => { void handleWelcomeFinished(); }} />
        ) : null}
        <PostFirstPickPromptManager />
      </View>
      </CompetitionProvider>
    </ErrorBoundary>
  );
}
